import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './ProductValue.css';

const ProductValue = () => {
    const [productData, setProductData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProductValue = async () => {
            try {
                setLoading(true);
                const response = await api.get('/api/dashboard/product-value');
                const result = response.data;
                console.log('Product Value API Response:', result);
                if (result && result.success && result.data) {
                    // Handle the actual API response format: { labels, high, avg, low }
                    const labels = result.data.labels || result.labels || [];
                    const high = result.data.high || result.high || [];
                    const avg = result.data.avg || result.avg || [];
                    const low = result.data.low || result.low || [];
                    // Transform to chart format
                    const chartData = labels.map((label, index) => ({
                        category: label || `Platform ${index + 1}`,
                        avgValue: Math.round(parseFloat(avg[index]) || 0),
                        highValue: Math.round(parseFloat(high[index]) || 0),
                        lowValue: Math.round(parseFloat(low[index]) || 0)
                    }));
                    setProductData(chartData);
                } else {
                    setProductData([]);
                }
            } catch (error) {
                console.error('Failed to fetch product value:', error);
                setProductData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchProductValue();
    }, []);

    // Calculate insights from actual data
    const allHighValues = productData.map(p => p.highValue).filter(v => v > 0);
    const allAvgValues = productData.map(p => p.avgValue).filter(v => v > 0);
    const allLowValues = productData.map(p => p.lowValue).filter(v => v > 0);

    const maxHighValue = allHighValues.length > 0 ? Math.max(...allHighValues) : 0;
    const avgOfAvgValues = allAvgValues.length > 0 ? Math.round(allAvgValues.reduce((a, b) => a + b, 0) / allAvgValues.length) : 0;
    const minLowValue = allLowValues.length > 0 ? Math.min(...allLowValues) : 0;

    const highestCategory = productData.find(p => p.highValue === maxHighValue)?.category || 'N/A';

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip">
                    <p className="label">{payload[0].payload.category}</p>
                    <p className="high" style={{ color: '#dc3545' }}>High: ${payload[0].payload.highValue}</p>
                    <p className="avg" style={{ color: '#ffc107' }}>Avg: ${payload[0].payload.avgValue}</p>
                    <p className="low" style={{ color: '#28a745' }}>Low: ${payload[0].payload.lowValue}</p>
                </div>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <div className="dashboard-card product-value-card">
                <div className="card-body">
                    <div className="card-header">
                        <h3 className="card-title">Product Value Analysis</h3>
                        <p className="card-subtitle">Price trends across product platforms</p>
                    </div>
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                        Loading product value data...
                    </div>
                </div>
            </div>
        );
    }

    if (productData.length === 0) {
        return (
            <div className="dashboard-card product-value-card">
                <div className="card-body">
                    <div className="card-header">
                        <h3 className="card-title">Product Value Analysis</h3>
                        <p className="card-subtitle">Price trends across product platforms</p>
                    </div>
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                        No data available
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-card product-value-card">
            <div className="card-body">
                <div className="card-header">
                    <h3 className="card-title">Product Value Analysis</h3>
                    <p className="card-subtitle">Price trends across product platforms</p>
                </div>

                <div className="chart-container">
                    <ResponsiveContainer width="100%" height={350}>
                        <LineChart
                            data={productData}
                            margin={{
                                top: 10,
                                right: productData.length === 1 ? 50 : 25,
                                left: productData.length === 1 ? 50 : 5,
                                bottom: 10
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                            <XAxis
                                dataKey="category"
                                stroke="#6c757d"
                                style={{ fontSize: '12px' }}
                            />
                            <YAxis
                                stroke="#6c757d"
                                style={{ fontSize: '12px' }}
                                label={{ value: 'Price ($)', angle: -90, position: 'insideLeft', style: { fontSize: '12px' } }}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend
                                wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                                iconType="line"
                            />
                            <Line
                                type="monotone"
                                dataKey="highValue"
                                stroke="#dc3545"
                                strokeWidth={3}
                                name="High Value"
                                dot={{ fill: '#dc3545', r: 5 }}
                                activeDot={{ r: 7 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="avgValue"
                                stroke="#ffc107"
                                strokeWidth={3}
                                name="Average Value"
                                dot={{ fill: '#ffc107', r: 5 }}
                                activeDot={{ r: 7 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="lowValue"
                                stroke="#28a745"
                                strokeWidth={3}
                                name="Low Value"
                                dot={{ fill: '#28a745', r: 5 }}
                                activeDot={{ r: 7 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                <div className="analysis-section">
                    <h4 className="analysis-title">Key Insights</h4>
                    <div className="insights-grid">
                        <div className="insight-item">
                            <span className="insight-icon">📈</span>
                            <div className="insight-content">
                                <p className="insight-label">Highest Value Platform</p>
                                <p className="insight-value">{highestCategory} (${maxHighValue})</p>
                            </div>
                        </div>
                        <div className="insight-item">
                            <span className="insight-icon">💰</span>
                            <div className="insight-content">
                                <p className="insight-label">Average Product Value</p>
                                <p className="insight-value">${avgOfAvgValues}</p>
                            </div>
                        </div>
                        <div className="insight-item">
                            <span className="insight-icon">📊</span>
                            <div className="insight-content">
                                <p className="insight-label">Price Range</p>
                                <p className="insight-value">${minLowValue} - ${maxHighValue}</p>
                            </div>
                        </div>
                    </div>
                    <p className="analysis-text">
                        {productData.length > 0
                            ? `${highestCategory} platform shows the highest value products ($${maxHighValue}). Average pricing across all platforms is $${avgOfAvgValues}.`
                            : 'Add products to see value analysis across platforms.'
                        }
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ProductValue;
